import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { api } from '../api';

const Settings: React.FC = () => {
  const [userEmail, setUserEmail] = useState('');
  const [isDemo, setIsDemo] = useState(false);
  const [coinbaseGuideOpen, setCoinbaseGuideOpen] = useState(false);
  const [coinbaseApiKey, setCoinbaseApiKey] = useState('');
  const [coinbaseApiSecret, setCoinbaseApiSecret] = useState('');
  const [coinbaseHasKeys, setCoinbaseHasKeys] = useState(false);
  const [coinbaseSaving, setCoinbaseSaving] = useState(false);
  const [coinbaseMsg, setCoinbaseMsg] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState('');
  const [notifyBrowser, setNotifyBrowser] = useState(false);
  const [notifyTelegram, setNotifyTelegram] = useState(true);
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [telegramConnected, setTelegramConnected] = useState(false);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramTesting, setTelegramTesting] = useState(false);
  const [telegramMsg, setTelegramMsg] = useState('');
  const [notifyTradeEntries, setNotifyTradeEntries] = useState(true);
  const [notifyDailySummary, setNotifyDailySummary] = useState(true);
  const [notifySignalAlerts, setNotifySignalAlerts] = useState(true);

  // Tax Reserve
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [taxFilingStatus, setTaxFilingStatus] = useState('single');
  const [taxIncomeBracket, setTaxIncomeBracket] = useState('50_100k');
  const [taxState, setTaxState] = useState('');
  const [taxRateOverride, setTaxRateOverride] = useState<number | null>(null);
  const [useCustomRate, setUseCustomRate] = useState(false);

  // Account Reset
  const [resetOpen, setResetOpen] = useState(false);
  const [resetCapital, setResetCapital] = useState(10000);
  const [resetConfirmation, setResetConfirmation] = useState('');
  const [resetting, setResetting] = useState(false);
  const [resetMsg, setResetMsg] = useState('');

  const FEDERAL_RATES: Record<string, number> = {
    under_50k: 12, '50_100k': 22, '100_200k': 24, '200_500k': 35, over_500k: 37,
  };
  const STATE_RATES: Record<string, number> = {
    AL: 5.0, AK: 0, AZ: 2.5, AR: 4.4, CA: 13.3, CO: 4.4, CT: 6.99, DE: 6.6, DC: 10.75,
    FL: 0, GA: 5.39, HI: 11.0, ID: 5.8, IL: 4.95, IN: 3.05, IA: 3.8, KS: 5.7, KY: 4.0,
    LA: 4.25, ME: 7.15, MD: 5.75, MA: 9.0, MI: 4.25, MN: 9.85, MS: 5.0, MO: 0, MT: 5.9,
    NE: 5.84, NV: 0, NH: 0, NJ: 10.75, NM: 5.9, NY: 10.9, NC: 4.5, ND: 2.5, OH: 3.5,
    OK: 4.75, OR: 9.9, PA: 3.07, RI: 5.99, SC: 6.4, SD: 0, TN: 0, TX: 0, UT: 4.65,
    VT: 8.75, VA: 5.75, WA: 7.0, WV: 5.12, WI: 7.65, WY: 0,
  };
  const STATE_NAMES: Record<string, string> = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
    FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
    IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
    ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
    MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
    NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
    NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
    PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
    TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
    WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  };

  const federalRate = FEDERAL_RATES[taxIncomeBracket] ?? 22;
  const stateRate = taxState ? (STATE_RATES[taxState] ?? 0) : 0;
  const totalRate = useCustomRate && taxRateOverride != null ? taxRateOverride : federalRate + stateRate;

  const saveTaxConfig = async (updates: Record<string, any>) => {
    if (isDemo) return;
    try { await api.updateConfig(updates); } catch {}
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const email = session?.user?.email || '';
      setUserEmail(email);
      setIsDemo(email === 'demo@tradesavvy.io');
    });
    // Load notification prefs from config
    api.getConfig().then((config: any) => {
      if (config.notify_browser !== undefined) setNotifyBrowser(config.notify_browser);
      if (config.notify_telegram !== undefined) setNotifyTelegram(config.notify_telegram);
      if (config.notify_email !== undefined) setNotifyEmail(config.notify_email);
      // Tax Reserve
      if (config.tax_reserve_enabled !== undefined) setTaxEnabled(config.tax_reserve_enabled);
      if (config.tax_filing_status) setTaxFilingStatus(config.tax_filing_status);
      if (config.tax_income_bracket) setTaxIncomeBracket(config.tax_income_bracket);
      if (config.tax_state !== undefined) setTaxState(config.tax_state || '');
      if (config.tax_rate_override != null && config.tax_rate_override > 0) {
        setTaxRateOverride(config.tax_rate_override);
        setUseCustomRate(true);
      }
    }).catch(() => {});
    // Load Coinbase key status
    api.getCoinbaseKeysStatus().then((s: any) => {
      setCoinbaseHasKeys(s.has_keys);
    }).catch(() => {});
    // Load Telegram connection status
    api.telegramStatus().then((status: any) => {
      setTelegramConnected(status.connected);
      if (status.notify_telegram !== undefined) setNotifyTelegram(status.notify_telegram);
      if (status.notify_trade_entries !== undefined) setNotifyTradeEntries(status.notify_trade_entries);
      if (status.notify_daily_summary !== undefined) setNotifyDailySummary(status.notify_daily_summary);
      if (status.notify_signal_alerts !== undefined) setNotifySignalAlerts(status.notify_signal_alerts);
    }).catch(() => {});
  }, []);

  const toggleNotification = async (field: string, current: boolean, setter: (v: boolean) => void) => {
    if (isDemo) return;
    const newVal = !current;
    setter(newVal);

    // Browser notifications: request permission when enabling
    if (field === 'notify_browser' && newVal) {
      if ('Notification' in window && Notification.permission === 'default') {
        const perm = await Notification.requestPermission();
        if (perm !== 'granted') {
          setter(false);
          return;
        }
      } else if ('Notification' in window && Notification.permission === 'denied') {
        setter(false);
        alert('Browser notifications are blocked. Please enable them in your browser settings.');
        return;
      }
    }

    try {
      await api.updateConfig({ [field]: newVal });
    } catch {
      setter(current); // revert on failure
    }
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Settings</div>
      </div>

      {isDemo && (
        <div style={{
          background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.25)',
          color: '#eab308', padding: '12px 16px', borderRadius: 8, fontSize: 13,
          marginBottom: 20, textAlign: 'center',
        }}>
          You are using the demo account. Settings cannot be changed. Sign up for your own account to customize.
        </div>
      )}

      <div className="grid-equal">
        {/* Coinbase API */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Coinbase API Connection</div>
            {coinbaseHasKeys ? (
              <span className="badge badge-green">Connected</span>
            ) : (
              <span className="badge badge-yellow">Not Connected</span>
            )}
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {coinbaseHasKeys ? (
                <div style={{
                  padding: '12px 16px', background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.15)',
                  borderRadius: 8, fontSize: 13, color: '#a1a1aa',
                }}>
                  API keys are stored securely (encrypted). To update, clear and re-enter.
                </div>
              ) : (
                <>
                  <div className="config-item">
                    <div className="config-label">API Key</div>
                    <input
                      type="password"
                      className="form-input"
                      placeholder="Enter your Coinbase API key"
                      style={{ width: '100%' }}
                      value={coinbaseApiKey}
                      onChange={(e) => setCoinbaseApiKey(e.target.value)}
                      disabled={isDemo}
                    />
                  </div>
                  <div className="config-item">
                    <div className="config-label">API Secret</div>
                    <input
                      type="password"
                      className="form-input"
                      placeholder="Enter your Coinbase API secret"
                      style={{ width: '100%' }}
                      value={coinbaseApiSecret}
                      onChange={(e) => setCoinbaseApiSecret(e.target.value)}
                      disabled={isDemo}
                    />
                  </div>
                </>
              )}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
                borderRadius: 8, fontSize: 12, color: '#a1a1aa'
              }}>
                <span style={{ color: '#ef4444', fontWeight: 700 }}>!</span>
                Never grant withdrawal permissions. Trade-only keys keep your funds safe.
              </div>

              {coinbaseMsg && (
                <div style={{
                  padding: '8px 12px', borderRadius: 6, fontSize: 12,
                  background: coinbaseMsg.includes('error') || coinbaseMsg.includes('Error') ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)',
                  border: `1px solid ${coinbaseMsg.includes('error') || coinbaseMsg.includes('Error') ? 'rgba(239,68,68,0.2)' : 'rgba(34,197,94,0.2)'}`,
                  color: coinbaseMsg.includes('error') || coinbaseMsg.includes('Error') ? '#ef4444' : '#22c55e',
                }}>
                  {coinbaseMsg}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                {coinbaseHasKeys ? (
                  <button
                    className="btn"
                    disabled={isDemo || coinbaseSaving}
                    onClick={async () => {
                      setCoinbaseSaving(true);
                      setCoinbaseMsg('');
                      try {
                        await api.clearCoinbaseKeys();
                        setCoinbaseHasKeys(false);
                        setCoinbaseMsg('API keys cleared.');
                      } catch (err: any) {
                        setCoinbaseMsg(err.message || 'Error clearing keys.');
                      }
                      setCoinbaseSaving(false);
                    }}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    {coinbaseSaving ? 'Clearing...' : 'Clear Keys'}
                  </button>
                ) : (
                  <button
                    className="btn"
                    disabled={isDemo || coinbaseSaving || !coinbaseApiKey || !coinbaseApiSecret}
                    onClick={async () => {
                      setCoinbaseSaving(true);
                      setCoinbaseMsg('');
                      try {
                        await api.saveCoinbaseKeys(coinbaseApiKey, coinbaseApiSecret);
                        setCoinbaseHasKeys(true);
                        setCoinbaseApiKey('');
                        setCoinbaseApiSecret('');
                        setCoinbaseMsg('API keys saved securely.');
                      } catch (err: any) {
                        setCoinbaseMsg(err.message || 'Error saving keys.');
                      }
                      setCoinbaseSaving(false);
                    }}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    {coinbaseSaving ? 'Saving...' : 'Save Keys'}
                  </button>
                )}
              </div>

              {/* Coinbase Setup Guide */}
              <div
                onClick={() => setCoinbaseGuideOpen(!coinbaseGuideOpen)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                  padding: '10px 12px', background: '#1a1c22', borderRadius: 8,
                  fontSize: 13, color: '#a1a1aa',
                }}
              >
                {coinbaseGuideOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                <span style={{ fontWeight: 600 }}>Setup Guide: Creating Coinbase API Keys</span>
              </div>
              {coinbaseGuideOpen && (
                <div style={{
                  padding: '12px 16px', background: '#1a1c22', borderRadius: '0 0 8px 8px',
                  fontSize: 12, color: '#a1a1aa', lineHeight: 1.8, marginTop: -8,
                }}>
                  <ol style={{ paddingLeft: 20, margin: 0 }}>
                    <li>Go to <strong>coinbase.com/settings/api</strong></li>
                    <li>Click <strong>"New API Key"</strong></li>
                    <li>Under <strong>Permissions</strong>, enable only:
                      <ul style={{ paddingLeft: 16, marginTop: 4, marginBottom: 4 }}>
                        <li style={{ color: '#22c55e' }}>View (wallet:accounts:read)</li>
                        <li style={{ color: '#22c55e' }}>Trade (wallet:trades:create, wallet:trades:read)</li>
                      </ul>
                    </li>
                    <li style={{ color: '#ef4444', fontWeight: 600 }}>DO NOT enable "Send" or "Withdraw" permissions</li>
                    <li>Copy the API Key and Secret — paste them into the fields above</li>
                    <li>Click Save Keys</li>
                  </ol>
                  <div style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(234,179,8,0.06)', border: '1px solid rgba(234,179,8,0.15)', borderRadius: 6, fontSize: 11, color: '#eab308' }}>
                    Paper trading does not require API keys. Keys are only needed for live trading.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Notifications */}
        <div className="card">
          <div className="card-header">
            <div className="card-title">Notifications</div>
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#e4e4e7' }}>Browser Notifications</div>
                  <div style={{ fontSize: 12, color: '#a1a1aa' }}>Push alerts for trades and bot events</div>
                </div>
                <div
                  onClick={() => toggleNotification('notify_browser', notifyBrowser, setNotifyBrowser)}
                  style={{
                    width: 36, height: 20, background: notifyBrowser ? '#22c55e' : '#3f3f46',
                    borderRadius: 10, position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 2, width: 16, height: 16, background: 'white', borderRadius: '50%',
                    transition: 'left 0.2s, right 0.2s',
                    ...(notifyBrowser ? { right: 2 } : { left: 2 }),
                  }} />
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#e4e4e7' }}>Telegram Alerts</div>
                  <div style={{ fontSize: 12, color: '#a1a1aa' }}>Real-time trade alerts via Telegram bot</div>
                </div>
                <div
                  onClick={() => toggleNotification('notify_telegram', notifyTelegram, setNotifyTelegram)}
                  style={{
                    width: 36, height: 20, background: notifyTelegram ? '#22c55e' : '#3f3f46',
                    borderRadius: 10, position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 2, width: 16, height: 16, background: 'white', borderRadius: '50%',
                    transition: 'left 0.2s, right 0.2s',
                    ...(notifyTelegram ? { right: 2 } : { left: 2 }),
                  }} />
                </div>
              </div>

              {/* Telegram Connection */}
              <div style={{
                padding: '12px 16px', background: '#1a1c22', borderRadius: 8,
                display: 'flex', flexDirection: 'column', gap: 12,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: telegramConnected ? '#22c55e' : '#3f3f46',
                    }} />
                    <span style={{ fontSize: 12, color: telegramConnected ? '#22c55e' : '#a1a1aa' }}>
                      {telegramConnected ? 'Connected' : 'Not connected'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {!telegramConnected ? (
                      <button
                        className="btn"
                        disabled={isDemo || telegramLoading}
                        onClick={async () => {
                          setTelegramLoading(true);
                          setTelegramMsg('');
                          try {
                            const result = await api.telegramConnect();
                            window.open(result.link_url, '_blank');
                            // Poll for connection confirmation
                            let attempts = 0;
                            const pollInterval = setInterval(async () => {
                              attempts++;
                              if (attempts > 20) {
                                clearInterval(pollInterval);
                                setTelegramLoading(false);
                                setTelegramMsg('Timed out. Press Start in Telegram, then refresh.');
                                return;
                              }
                              try {
                                const status = await api.telegramStatus();
                                if (status.connected) {
                                  setTelegramConnected(true);
                                  clearInterval(pollInterval);
                                  setTelegramLoading(false);
                                  setTelegramMsg('Connected!');
                                }
                              } catch {}
                            }, 3000);
                          } catch (err: any) {
                            setTelegramMsg(err.message || 'Failed to generate link');
                            setTelegramLoading(false);
                          }
                        }}
                        style={{ fontSize: 12, borderColor: '#3b82f6', color: '#3b82f6' }}
                      >
                        {telegramLoading ? 'Waiting for Telegram...' : 'Connect Telegram'}
                      </button>
                    ) : (
                      <>
                        <button
                          className="btn"
                          disabled={isDemo || telegramTesting}
                          onClick={async () => {
                            setTelegramTesting(true);
                            setTelegramMsg('');
                            try {
                              await api.testTelegram();
                              setTelegramMsg('Test sent!');
                            } catch (err: any) {
                              setTelegramMsg(err.message || 'Test failed');
                            }
                            setTelegramTesting(false);
                          }}
                          style={{ fontSize: 12, borderColor: '#3b82f6', color: '#3b82f6' }}
                        >
                          {telegramTesting ? 'Sending...' : 'Send Test'}
                        </button>
                        <button
                          className="btn"
                          disabled={isDemo}
                          onClick={async () => {
                            try {
                              await api.telegramDisconnect();
                              setTelegramConnected(false);
                              setTelegramMsg('Disconnected');
                            } catch (err: any) {
                              setTelegramMsg(err.message || 'Failed');
                            }
                          }}
                          style={{ fontSize: 12, borderColor: '#ef4444', color: '#ef4444' }}
                        >
                          Disconnect
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {telegramMsg && (
                  <div style={{ fontSize: 12, color: telegramMsg.includes('Connected') || telegramMsg.includes('sent') ? '#22c55e' : '#ef4444' }}>
                    {telegramMsg}
                  </div>
                )}

                {/* Granular notification toggles — only when connected */}
                {telegramConnected && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 4, paddingTop: 12, borderTop: '1px solid #27272a' }}>
                    <div style={{ fontSize: 11, color: '#71717a', textTransform: 'uppercase', fontWeight: 600 }}>Notification Types</div>
                    {[
                      { label: 'Trade Entries & Exits', desc: 'Bot trade executions, stops, take profits', field: 'notify_trade_entries', state: notifyTradeEntries, setter: setNotifyTradeEntries },
                      { label: 'Daily Summaries', desc: 'Daily portfolio summary at 8pm UTC', field: 'notify_daily_summary', state: notifyDailySummary, setter: setNotifyDailySummary },
                      { label: 'Watchlist Signal Alerts', desc: 'BUY/SELL signals from your alert rules', field: 'notify_signal_alerts', state: notifySignalAlerts, setter: setNotifySignalAlerts },
                    ].map(item => (
                      <div key={item.field} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: 12, color: '#e4e4e7' }}>{item.label}</div>
                          <div style={{ fontSize: 11, color: '#71717a' }}>{item.desc}</div>
                        </div>
                        <div
                          onClick={() => toggleNotification(item.field, item.state, item.setter)}
                          style={{
                            width: 36, height: 20, background: item.state ? '#22c55e' : '#3f3f46',
                            borderRadius: 10, position: 'relative', cursor: isDemo ? 'not-allowed' : 'pointer', transition: 'background 0.2s',
                          }}
                        >
                          <div style={{
                            position: 'absolute', top: 2, width: 16, height: 16, background: 'white', borderRadius: '50%',
                            transition: 'left 0.2s, right 0.2s',
                            ...(item.state ? { right: 2 } : { left: 2 }),
                          }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: '#e4e4e7' }}>Daily Email Digest</div>
                  <div style={{ fontSize: 12, color: '#a1a1aa' }}>Summary of bot activity at 8pm UTC</div>
                </div>
                <div
                  onClick={() => toggleNotification('notify_email', notifyEmail, setNotifyEmail)}
                  style={{
                    width: 36, height: 20, background: notifyEmail ? '#22c55e' : '#3f3f46',
                    borderRadius: 10, position: 'relative', cursor: 'pointer', transition: 'background 0.2s',
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 2, width: 16, height: 16, background: 'white', borderRadius: '50%',
                    transition: 'left 0.2s, right 0.2s',
                    ...(notifyEmail ? { right: 2 } : { left: 2 }),
                  }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tax Reserve */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div className="card-title">Tax Reserve</div>
            <div style={{ fontSize: 12, color: '#a1a1aa', marginTop: 2 }}>Automatically set aside estimated taxes on realized gains</div>
          </div>
          <div
            onClick={() => {
              if (isDemo) return;
              const newVal = !taxEnabled;
              setTaxEnabled(newVal);
              saveTaxConfig({ tax_reserve_enabled: newVal });
            }}
            style={{
              width: 36, height: 20, background: taxEnabled ? '#22c55e' : '#3f3f46',
              borderRadius: 10, position: 'relative', cursor: isDemo ? 'not-allowed' : 'pointer', transition: 'background 0.2s',
            }}
          >
            <div style={{
              position: 'absolute', top: 2, width: 16, height: 16, background: 'white', borderRadius: '50%',
              transition: 'left 0.2s, right 0.2s',
              ...(taxEnabled ? { right: 2 } : { left: 2 }),
            }} />
          </div>
        </div>

        {taxEnabled && (
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* Dropdowns */}
            <div className="grid-3col">
              <div>
                <div style={{ fontSize: 12, color: '#a1a1aa', marginBottom: 6 }}>Filing Status</div>
                <select
                  className="form-input"
                  style={{ width: '100%' }}
                  value={taxFilingStatus}
                  disabled={isDemo}
                  onChange={e => {
                    setTaxFilingStatus(e.target.value);
                    saveTaxConfig({ tax_filing_status: e.target.value });
                  }}
                >
                  <option value="single">Single</option>
                  <option value="married_joint">Married Filing Jointly</option>
                  <option value="head_household">Head of Household</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#a1a1aa', marginBottom: 6 }}>Annual Income</div>
                <select
                  className="form-input"
                  style={{ width: '100%' }}
                  value={taxIncomeBracket}
                  disabled={isDemo}
                  onChange={e => {
                    setTaxIncomeBracket(e.target.value);
                    saveTaxConfig({ tax_income_bracket: e.target.value });
                  }}
                >
                  <option value="under_50k">Under $50K</option>
                  <option value="50_100k">$50K - $100K</option>
                  <option value="100_200k">$100K - $200K</option>
                  <option value="200_500k">$200K - $500K</option>
                  <option value="over_500k">Over $500K</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 12, color: '#a1a1aa', marginBottom: 6 }}>State</div>
                <select
                  className="form-input"
                  style={{ width: '100%' }}
                  value={taxState}
                  disabled={isDemo}
                  onChange={e => {
                    setTaxState(e.target.value);
                    saveTaxConfig({ tax_state: e.target.value });
                  }}
                >
                  <option value="">No state tax</option>
                  {Object.entries(STATE_NAMES).sort((a, b) => a[1].localeCompare(b[1])).map(([code, name]) => (
                    <option key={code} value={code}>{name}{STATE_RATES[code] === 0 ? ' (0%)' : ''}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Rate Breakdown */}
            {!useCustomRate && (
              <div style={{
                background: '#1a1c22', borderRadius: 8, padding: '12px 16px',
                display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#a1a1aa' }}>Federal Rate</span>
                  <span style={{ color: '#e4e4e7' }}>{federalRate.toFixed(1)}%</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#a1a1aa' }}>State Rate</span>
                  <span style={{ color: '#e4e4e7' }}>+{stateRate.toFixed(1)}%</span>
                </div>
                <div style={{ borderTop: '1px solid #27272a', paddingTop: 6, display: 'flex', justifyContent: 'space-between', fontWeight: 600 }}>
                  <span style={{ color: '#e4e4e7' }}>Estimated Total Rate</span>
                  <span style={{ color: '#22c55e' }}>{totalRate.toFixed(1)}%</span>
                </div>
              </div>
            )}

            {/* Custom Rate Override */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="checkbox"
                checked={useCustomRate}
                disabled={isDemo}
                onChange={e => {
                  const checked = e.target.checked;
                  setUseCustomRate(checked);
                  if (!checked) {
                    setTaxRateOverride(null);
                    saveTaxConfig({ tax_rate_override: null });
                  }
                }}
                style={{ accentColor: '#22c55e' }}
              />
              <span style={{ fontSize: 13, color: '#a1a1aa' }}>Override with custom rate:</span>
              {useCustomRate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="number"
                    className="form-input"
                    style={{ width: 70, textAlign: 'center' }}
                    value={taxRateOverride ?? ''}
                    disabled={isDemo}
                    min={0}
                    max={60}
                    step={0.1}
                    onChange={e => {
                      const val = e.target.value ? parseFloat(e.target.value) : null;
                      setTaxRateOverride(val);
                    }}
                    onBlur={() => {
                      if (taxRateOverride != null) {
                        saveTaxConfig({ tax_rate_override: taxRateOverride });
                      }
                    }}
                  />
                  <span style={{ fontSize: 13, color: '#a1a1aa' }}>%</span>
                </div>
              )}
              {useCustomRate && taxRateOverride != null && (
                <span style={{ fontSize: 12, color: '#22c55e', marginLeft: 8 }}>
                  Using {taxRateOverride.toFixed(1)}% total rate
                </span>
              )}
            </div>

            {/* Disclaimer */}
            <div style={{
              display: 'flex', gap: 8, padding: '10px 12px',
              background: 'rgba(234,179,8,0.06)', border: '1px solid rgba(234,179,8,0.15)',
              borderRadius: 8, fontSize: 12, color: '#a1a1aa', lineHeight: 1.6,
            }}>
              <span style={{ color: '#eab308', fontWeight: 700, flexShrink: 0 }}>!</span>
              <span>
                This is an estimate only. Short-term crypto gains (held &lt; 1 year) are taxed as ordinary income.
                Rates shown are marginal rates for your income bracket. Consult a tax professional for personalized advice.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Account */}
      <div className="card">
        <div className="card-header">
          <div className="card-title">Account</div>
        </div>
        <div className="card-body">
          <div className="grid-3col" style={{ marginBottom: 16 }}>
            <div className="config-item">
              <div className="config-label">Email</div>
              <div style={{ fontSize: 14, color: '#e4e4e7' }}>{userEmail || 'Loading...'}</div>
            </div>
            <div className="config-item">
              <div className="config-label">Timezone</div>
              <div style={{ fontSize: 14, color: '#e4e4e7' }}>America/Los_Angeles (PST)</div>
            </div>
            <div className="config-item">
              <div className="config-label">Starting Capital</div>
              <div style={{ fontSize: 14, color: '#e4e4e7' }}>$10,000 (paper) / $0 (live)</div>
            </div>
          </div>

          {/* Email & Password Update */}
          <div className="grid-2col">
            <div className="config-item">
              <div className="config-label">Update Email</div>
              <input
                type="email"
                className="form-input"
                placeholder="New email address"
                value={newEmail}
                onChange={e => setNewEmail(e.target.value)}
                style={{ width: '100%', marginBottom: 8 }}
              />
              <button
                className="btn btn-primary"
                disabled={isDemo || !newEmail || emailSaving}
                onClick={async () => {
                  if (isDemo) return;
                  setEmailSaving(true);
                  setEmailMsg('');
                  try {
                    const { error } = await supabase.auth.updateUser({ email: newEmail });
                    if (error) throw error;
                    setEmailMsg('Confirmation email sent to your new address. Check your inbox.');
                    setNewEmail('');
                  } catch (err: any) {
                    setEmailMsg(err.message || 'Failed to update email');
                  }
                  setEmailSaving(false);
                }}
                style={{ fontSize: 12 }}
              >
                {emailSaving ? 'Sending...' : 'Update Email'}
              </button>
              {emailMsg && (
                <div style={{ fontSize: 11, color: emailMsg.includes('Confirmation') ? '#22c55e' : '#ef4444', marginTop: 6 }}>
                  {emailMsg}
                </div>
              )}
            </div>
            <div className="config-item">
              <div className="config-label">Update Password</div>
              <input
                type="password"
                className="form-input"
                placeholder="New password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                style={{ width: '100%', marginBottom: 8 }}
              />
              <button
                className="btn btn-primary"
                disabled={isDemo || !newPassword || newPassword.length < 6 || pwSaving}
                onClick={async () => {
                  if (isDemo) return;
                  setPwSaving(true);
                  setPwMsg('');
                  try {
                    const { error } = await supabase.auth.updateUser({ password: newPassword });
                    if (error) throw error;
                    setPwMsg('Password updated successfully.');
                    setNewPassword('');
                  } catch (err: any) {
                    setPwMsg(err.message || 'Failed to update password');
                  }
                  setPwSaving(false);
                }}
                style={{ fontSize: 12 }}
              >
                {pwSaving ? 'Updating...' : 'Update Password'}
              </button>
              {newPassword && newPassword.length < 6 && (
                <div style={{ fontSize: 11, color: '#eab308', marginTop: 6 }}>Minimum 6 characters</div>
              )}
              {pwMsg && (
                <div style={{ fontSize: 11, color: pwMsg.includes('success') ? '#22c55e' : '#ef4444', marginTop: 6 }}>
                  {pwMsg}
                </div>
              )}
            </div>
          </div>

          {/* Reset Paper Account */}
          <div style={{ borderTop: '1px solid #2a2d35', marginTop: 20, paddingTop: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#e4e4e7' }}>Reset Paper Account</div>
                <div style={{ fontSize: 12, color: '#a1a1aa', marginTop: 2 }}>Clear all paper trading data and start fresh</div>
              </div>
              <button
                className="btn"
                disabled={isDemo}
                onClick={() => { setResetOpen(!resetOpen); setResetMsg(''); setResetConfirmation(''); }}
                style={{ borderColor: '#ef4444', color: '#ef4444', opacity: isDemo ? 0.5 : 1 }}
              >
                {resetOpen ? 'Cancel' : 'Reset Account'}
              </button>
            </div>

            {resetOpen && (
              <div style={{
                marginTop: 16, padding: 20, background: 'rgba(239,68,68,0.04)',
                border: '1px solid rgba(239,68,68,0.15)', borderRadius: 8,
              }}>
                <div style={{ fontSize: 13, color: '#a1a1aa', marginBottom: 16, lineHeight: 1.6 }}>
                  This will permanently delete:
                  <ul style={{ paddingLeft: 20, marginTop: 8 }}>
                    <li>All trade history</li>
                    <li>All open positions</li>
                    <li>All equity snapshots (chart data)</li>
                    <li>All bot events (activity log)</li>
                    <li>All tax payment records</li>
                  </ul>
                  <div style={{ marginTop: 8 }}>
                    Strategy configuration, portfolio holdings, and watchlists will be preserved.
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 16, marginBottom: 16, alignItems: 'flex-end' }}>
                  <div>
                    <div style={{ fontSize: 12, color: '#a1a1aa', marginBottom: 6 }}>New Starting Capital</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ color: '#a1a1aa' }}>$</span>
                      <input
                        type="number" className="form-input"
                        style={{ width: 120, textAlign: 'center' }}
                        value={resetCapital} min={100} max={1000000} step={100}
                        onChange={e => setResetCapital(parseFloat(e.target.value) || 10000)}
                      />
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: 12, color: '#a1a1aa', marginBottom: 6 }}>
                      Type <strong style={{ color: '#ef4444' }}>RESET</strong> to confirm
                    </div>
                    <input
                      type="text" className="form-input"
                      style={{ width: 120, textAlign: 'center' }}
                      value={resetConfirmation} placeholder="RESET"
                      onChange={e => setResetConfirmation(e.target.value)}
                    />
                  </div>
                  <button
                    className="btn"
                    disabled={resetConfirmation !== 'RESET' || resetting}
                    onClick={async () => {
                      setResetting(true);
                      setResetMsg('');
                      try {
                        await api.resetAccount(resetCapital, resetConfirmation);
                        setResetMsg('Account reset successfully. Reloading...');
                        setResetOpen(false);
                        setResetConfirmation('');
                        setTimeout(() => window.location.reload(), 1500);
                      } catch (err: any) {
                        setResetMsg(err.message || 'Reset failed');
                      }
                      setResetting(false);
                    }}
                    style={{
                      background: resetConfirmation === 'RESET' ? '#ef4444' : undefined,
                      borderColor: resetConfirmation === 'RESET' ? '#ef4444' : undefined,
                      color: resetConfirmation === 'RESET' ? '#fff' : undefined,
                    }}
                  >
                    {resetting ? 'Resetting...' : 'Confirm Reset'}
                  </button>
                </div>

                {resetMsg && (
                  <div style={{ fontSize: 12, color: resetMsg.includes('success') ? '#22c55e' : '#ef4444' }}>
                    {resetMsg}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
